import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaFacebook, FaLinkedin, FaTwitter, FaYoutube, FaWhatsapp } from 'react-icons/fa';
import axios from 'axios';

const API_URL = 'https://aamirsaba.com/api';

const Footer = () => {
  const [footerData, setFooterData] = useState({
    companyName: 'AAMISABA LTD',
    tagline: 'Digital transformation & IT solutions provider serving clients across UK, USA, and globally.',
    copyrightText: 'All rights reserved. UK & USA Registered Company.',
    facebook: 'https://facebook.com/aamirsaba',
    linkedin: 'https://linkedin.com/company/aamirsaba',
    twitter: 'https://twitter.com/aamirsaba',
    youtube: 'https://youtube.com/aamirsaba',
    whatsapp: '+1 (XXX) XXX-XXXX',
    email: 'info@aamirsaba.com',
    phone: '+44 7362046611',
    address: 'London, United Kingdom'
  });

  const [footerMenu, setFooterMenu] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load footer menu from API
  const loadFooterMenu = () => {
    axios.get(`${API_URL}/menu/footer`)
      .then(response => {
        console.log('Footer menu loaded from API:', response.data);
        if (response.data && response.data.length > 0) {
          setFooterMenu(response.data);
        } else {
          // Fallback
          setFooterMenu(getDefaultFooterMenu());
        }
        setLoading(false);
      })
      .catch(error => {
        console.error('Error loading footer menu:', error);
        setFooterMenu(getDefaultFooterMenu());
        setLoading(false);
      });
  };

  const getDefaultFooterMenu = () => {
    return [
      { 
        id: 'services', 
        label: 'Services', 
        children: [
          { label: 'Power BI Training', url: '/services' },
          { label: 'ITIL 4 Certification', url: '/services' },
          { label: 'WhatsApp Automation', url: '/services' },
          { label: 'SEO Automation', url: '/services' },
          { label: 'Custom Development', url: '/services' }
        ] 
      },
      { 
        id: 'company', 
        label: 'Company', 
        children: [
          { label: 'About Us', url: '/about' },
          { label: 'Blog', url: '/blog' },
          { label: 'Contact', url: '/contact' }
        ] 
      },
      { 
        id: 'policies', 
        label: 'Policies', 
        children: [
          { label: 'Refund Policy', url: '/refund-policy' },
          { label: 'Privacy Policy', url: '/privacy-policy' },
          { label: 'Terms of Service', url: '/terms' },
          { label: 'Cancellation Policy', url: '/cancellation-policy' }
        ] 
      },
      { 
        id: 'contactus', 
        label: 'Contact Us', 
        children: [
          { label: '📧 info@aamirsaba.com', url: 'mailto:info@aamirsaba.com' },
          { label: '📞 +44 7362046611', url: 'tel:+447362046611' },
          { label: '📍 London, United Kingdom', url: '#' },
          { label: 'Get a Free Consultation', url: '/contact' }
        ] 
      }
    ];
  };

  useEffect(() => {
    // Load footer data
    axios.get(`${API_URL}/footer`)
      .then(response => {
        setFooterData(prev => ({ ...prev, ...response.data }));
      })
      .catch(error => console.error('Error loading footer:', error));

    // Load footer menu
    loadFooterMenu();

    // Listen for storage changes (for when admin updates)
    const handleStorageChange = (e) => {
      if (e.key === 'footerMenuUpdated') {
        console.log('Footer menu updated, reloading...');
        loadFooterMenu();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Listen for custom event from admin
  useEffect(() => {
    const handleFooterMenuUpdate = () => {
      console.log('Footer menu update event received');
      loadFooterMenu();
    };
    window.addEventListener('footerMenuUpdated', handleFooterMenuUpdate);
    return () => window.removeEventListener('footerMenuUpdated', handleFooterMenuUpdate);
  }, []);

  if (loading) {
    return (
      <footer className="bg-darkNavy text-gray-300 pt-16 pb-8">
        <div className="container mx-auto px-6 text-center text-gray-500">
          Loading footer...
        </div>
      </footer>
    );
  }

  return (
    <footer className="bg-darkNavy text-gray-300 pt-16 pb-8">
      <div className="container mx-auto px-6">
        {/* Brand Row */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold text-white mb-4">
            {footerData.companyName || 'AamirSaba'}
          </h3>
          <p className="text-sm leading-relaxed max-w-md">{footerData.tagline}</p>
          <div className="flex space-x-4 mt-4">
            {footerData.facebook && (
              <a href={footerData.facebook} target="_blank" rel="noopener noreferrer" className="text-xl hover:text-cyan transition">
                <FaFacebook />
              </a>
            )}
            {footerData.linkedin && (
              <a href={footerData.linkedin} target="_blank" rel="noopener noreferrer" className="text-xl hover:text-cyan transition">
                <FaLinkedin />
              </a>
            )}
            {footerData.twitter && (
              <a href={footerData.twitter} target="_blank" rel="noopener noreferrer" className="text-xl hover:text-cyan transition">
                <FaTwitter />
              </a>
            )}
            {footerData.youtube && (
              <a href={footerData.youtube} target="_blank" rel="noopener noreferrer" className="text-xl hover:text-cyan transition">
                <FaYoutube />
              </a>
            )}
            {footerData.whatsapp && (
              <a href={`https://wa.me/${footerData.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-xl hover:text-cyan transition">
                <FaWhatsapp />
              </a>
            )}
          </div>
        </div>

        {/* Footer Menu Columns */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 border-t border-gray-800 pt-10">
          {footerMenu.map((column) => (
            <div key={column.id}>
              <h4 className="text-white font-semibold mb-3">{column.label}</h4>
              <ul className="space-y-2 text-sm">
                {column.children && column.children.length > 0 ? (
                  column.children.map((child, index) => (
                    <li key={index}>
                      {child.url && child.url.startsWith('mailto:') || child.url && child.url.startsWith('tel:') ? (
                        <a href={child.url} className="hover:text-cyan transition text-gray-400 hover:text-cyan">
                          {child.label}
                        </a>
                      ) : (
                        <Link to={child.url || '#'} className="hover:text-cyan transition text-gray-400 hover:text-cyan">
                          {child.label}
                        </Link>
                      )}
                    </li>
                  ))
                ) : (
                  <li className="text-gray-500 text-xs">No links</li>
                )}
              </ul>
            </div>
          ))}
        </div>

        {/* Copyright */}
        <div className="border-t border-gray-800 mt-10 pt-8 text-center text-xs">
          <p>&copy; {new Date().getFullYear()} {footerData.companyName || 'AamirSaba'}. {footerData.copyrightText || 'All rights reserved. UK & USA Registered Company.'}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
